import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  AlertTriangle,
  HelpCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Share2,
  Link2,
  Globe,
  Lock,
  Info,
  FileDown,
  Eye,
  EyeOff,
  Twitter,
  Trash2,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ConfidenceMeter from "@/components/features/ConfidenceMeter";
import { ReportBadge } from "@/components/features/ReportBadge";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_API_URL}/api`;

const VERDICT_ICONS = {
  REAL: ShieldCheck,
  FAKE: AlertTriangle,
  SUSPICIOUS: AlertTriangle,
  UNCERTAIN: HelpCircle,
};
const VERDICT_COLORS = {
  REAL: {
    text: "text-verdict-real",
    bg: "bg-verdict-real/10",
    border: "border-verdict-real/30",
  },
  FAKE: {
    text: "text-verdict-fake",
    bg: "bg-verdict-fake/10",
    border: "border-verdict-fake/30",
  },
  SUSPICIOUS: {
    text: "text-verdict-uncertain",
    bg: "bg-verdict-uncertain/10",
    border: "border-verdict-uncertain/30",
  },
  UNCERTAIN: {
    text: "text-verdict-uncertain",
    bg: "bg-verdict-uncertain/10",
    border: "border-verdict-uncertain/30",
  },
};

const VERDICT_HEX = {
  REAL: "#22c55e",
  FAKE: "#ef4444",
  SUSPICIOUS: "#f59e0b",
  UNCERTAIN: "#f59e0b",
};

function ScoreBox({ label, value, color }) {
  return (
    <div
      className="border border-border rounded-sm p-3"
      data-testid={`score-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
        {label}
      </div>
      <div className={`text-xl font-heading font-bold ${color}`}>{value}%</div>
    </div>
  );
}

const GENERATOR_LABELS = {
  midjourney: "Midjourney",
  stablediffusion: "Stable Diffusion",
  dalle: "DALL-E",
  flux: "Flux",
  sora: "Sora",
  stable_video_diffusion: "Stable Video Diffusion",
  pika: "Pika",
  runway: "Runway",
  luma: "Luma",
  kling: "Kling",
  hailuo: "Hailuo",
  pixverse: "PixVerse",
  hedra: "Hedra",
  hunyuan: "HunYuan",
  mochi: "Mochi",
  recraft: "Recraft",
  leonardo: "Leonardo",
  luminagpt: "LuminaGPT",
  haiper: "Haiper",
  hallo: "Hallo",
  cogvideos: "CogVideo",
  sadtalker: "SadTalker",
  liveportrait: "LivePortrait",
  aniportrait: "AniPortrait",
  makeittalk: "MakeItTalk",
  pyramidflows: "PyramidFlows",
  mcnet: "MCNet",
  other_image_generators: "Other Image Gen",
  other_video_generators: "Other Video Gen",
};

function GenerationSources({ gens, t }) {
  const [expanded, setExpanded] = useState(false);
  const INITIAL_SHOW = 6;
  const visible = expanded ? gens : gens.slice(0, INITIAL_SHOW);
  return (
    <div data-testid="generation-sources">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
          {t("results.generationSources")}
        </span>
        <div className="relative group">
          <Info className="w-3.5 h-3.5 text-muted-foreground/50 cursor-help" />
          <div className="absolute left-5 top-0 z-50 hidden group-hover:block w-56 rounded-md border border-border bg-popover text-popover-foreground text-xs p-2.5 shadow-lg leading-relaxed">
            {t("results.generationSourcesTooltip")}
          </div>
        </div>
      </div>
      {gens.length === 0 ? (
        <div className="text-xs font-mono text-muted-foreground/50 italic px-1">
          {t("results.noGenerationSources")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {visible.map(([gen, score]) => {
              const pct = (score * 100).toFixed(1);
              const isHigh = score >= 0.5;
              const isMed = score >= 0.1;
              return (
                <div
                  key={gen}
                  className={`flex items-center justify-between px-3 py-2 rounded-sm border text-xs font-mono ${
                    isHigh
                      ? "border-amber-500/30 bg-amber-500/5"
                      : isMed
                        ? "border-border bg-secondary/50"
                        : "border-border/50 bg-card"
                  }`}
                >
                  <span
                    className={
                      isHigh
                        ? "text-amber-400 font-semibold"
                        : "text-muted-foreground"
                    }
                  >
                    {GENERATOR_LABELS[gen] || gen}
                  </span>
                  <span
                    className={`ml-2 tabular-nums ${isHigh ? "text-amber-400 font-bold" : "text-muted-foreground"}`}
                  >
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
          {gens.length > INITIAL_SHOW && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 mt-3 text-xs font-mono text-brand-primary hover:text-brand-primary/80 transition-colors"
              data-testid="toggle-sources"
            >
              {expanded ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
              {expanded ? t("results.seeLess") : t("results.seeMore")}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function TechRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="border border-border rounded-sm p-4">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
        {label}
      </div>
      <p className="text-sm leading-relaxed">{value}</p>
    </div>
  );
}

export default function Results({ isSharedView = false }) {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const printRef = useRef(null);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [showThumb, setShowThumb] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState(null);
  const [feedbackSaving, setFeedbackSaving] = useState(false);

  // Close lightbox on Escape
  useEffect(() => {
    if (!showThumb) return;
    const handler = (e) => {
      if (e.key === "Escape") setShowThumb(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showThumb]);

  // On the share page: ANY owner can toggle (privacy right, not plan-gated)
  // On the results page: Pro/Business/Unlimited plan required
  const isOwner = !!(user && result && user.user_id === result?.user_id);
  const canToggleVisibility = isSharedView
    ? isOwner
    : !!(user && ["pro", "business", "unlimited"].includes(user.plan));
  const currentLang = i18n.language?.split("-")[0] || "id";

  useEffect(() => {
    const fetchResult = async () => {
      setLoading(true);
      try {
        if (isSharedView) {
          // Share page — go directly to share endpoint, skip private analysis fetch
          const res = await axios.get(`${API}/share/${id}?lang=${currentLang}`);
          setResult(res.data);
          setIsPublic(true);
          setFeedbackValue(null);
        } else {
          const res = await axios.get(
            `${API}/analysis/${id}?lang=${currentLang}`,
            { withCredentials: true },
          );
          setResult(res.data);
          setIsPublic(res.data.is_public ?? true);
          setFeedbackValue(res.data.user_feedback || null);
        }
      } catch {
        toast.error(t("results.notFound"));
      } finally {
        setLoading(false);
      }
    };

    // If navigating with state result and language matches, use it
    if (
      location.state?.result &&
      location.state.result.displayed_language === currentLang
    ) {
      setResult(location.state.result);
      setIsPublic(location.state.result.is_public ?? true);
      setFeedbackValue(location.state.result.user_feedback || null);
      setLoading(false);
      return;
    }

    fetchResult();
  }, [id, currentLang, isSharedView, location.state, t]);

  const handleToggleVisibility = async () => {
    if (!canToggleVisibility || !result) return;
    setToggling(true);
    try {
      await axios.patch(
        `${API}/analysis/${result.id}/visibility`,
        { is_public: !isPublic },
        { withCredentials: true },
      );
      setIsPublic(!isPublic);
      toast.success(isPublic ? t("results.private") : t("results.public"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setToggling(false);
    }
  };

  const handleArchive = async () => {
    if (!result || archiving) return;
    if (!window.confirm(t("results.archiveConfirm")))
      return;
    setArchiving(true);
    try {
      await axios.delete(`${API}/history/${result.id}`, {
        withCredentials: true,
      });
      toast.success(t("results.archiveSuccess"));
      navigate("/history");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
      setArchiving(false);
    }
  };

  const canSubmitFeedback =
    !isSharedView && !!(user && result && user.user_id === result.user_id);

  const handleSubmitFeedback = async (value) => {
    if (!canSubmitFeedback || feedbackSaving) return;
    setFeedbackSaving(true);
    try {
      const res = await axios.post(
        `${API}/analysis/${result.id}/feedback`,
        { feedback: value },
        { withCredentials: true },
      );
      setFeedbackValue(res.data?.feedback || value);
      setResult((prev) =>
        prev
          ? {
              ...prev,
              user_feedback: res.data?.feedback || value,
              ground_truth_label:
                res.data?.ground_truth_label || prev.ground_truth_label,
              label_source: res.data?.label_source || prev.label_source,
              is_training_eligible:
                typeof res.data?.is_training_eligible === "boolean"
                  ? res.data.is_training_eligible
                  : prev.is_training_eligible,
            }
          : prev,
      );
      toast.success(t("results.feedbackSaved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("results.feedbackError"));
    } finally {
      setFeedbackSaving(false);
    }
  };

  const getShareUrl = () => {
    const shareId = result?.share_id;
    if (!shareId) return null;
    return `${window.location.origin}/share/${shareId}`;
  };

  const handleShare = () => {
    if (!isPublic && !isSharedView) {
      toast.info(t("results.makePublicFirst"));
      return;
    }
    const url = getShareUrl();
    if (!url) {
      toast.error(t("results.shareIdMissing"));
      return;
    }
    navigator.clipboard
      ?.writeText(url)
      .then(() => toast.success(t("results.linkCopied")))
      .catch(() => toast.info(url));
  };

  const handleShareTwitter = () => {
    const url = getShareUrl();
    if (!url) return;
    const v = result?.verdict?.toUpperCase();
    const conf = Math.round(result.confidence);
    const text =
      v === "FAKE"
        ? t("results.shareTextFake", { confidence: conf })
        : t("results.shareTextOther", { verdict: v, confidence: conf });
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(tweetUrl, "_blank", "noopener");
  };

  const handleShareWhatsApp = () => {
    const url = getShareUrl();
    if (!url) return;
    const v = result?.verdict?.toUpperCase();
    const conf = Math.round(result.confidence);
    const text =
      v === "FAKE"
        ? t("results.shareTextFake", { confidence: conf })
        : t("results.shareTextOther", { verdict: v, confidence: conf });
    const fullText = `${text}\n\n${result.summary}\n\n${url}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(fullText)}`,
      "_blank",
      "noopener",
    );
  };

  const handleDownloadPdf = async () => {
    if (!result) return;
    try {
      toast.info(t("results.pdfGenerating"));
      const isShared = !user || isSharedView;
      const endpoint =
        isShared && result.share_id
          ? `${API}/share/${result.share_id}/pdf`
          : `${API}/analysis/${result.id}/pdf`;
      const res = await axios.get(endpoint, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `veriksa-${result.id || result.share_id}-bilingual.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("results.pdfSuccess"));
    } catch (err) {
      console.error("PDF error:", err);
      toast.error(t("results.pdfError"));
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground font-mono">
            {t("common.loading")}
          </p>
        </div>
      </main>
    );
  }

  if (!result) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-sm px-6">
          <div className="w-14 h-14 rounded-full bg-secondary border border-border flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6 text-muted-foreground/50" />
          </div>
          <div className="space-y-1.5">
            <h2 className="font-heading text-lg font-semibold tracking-tight">
              {t("results.notFoundTitle")}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("results.notFoundDesc")}
            </p>
          </div>
          <Link to="/analyze" className="block mt-4">
            <Button className="rounded-md bg-brand-primary text-white">
              {t("results.newAnalysis")}
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  const verdict = result.verdict?.toUpperCase() || "UNCERTAIN";
  const VIcon = VERDICT_ICONS[verdict] || HelpCircle;
  const colors = VERDICT_COLORS[verdict] || VERDICT_COLORS.UNCERTAIN;
  const tech = result.technical_details || {};

  const techFields = [
    { key: "facial_analysis", label: t("results.facial") },
    { key: "temporal_consistency", label: t("results.temporal") },
    { key: "lighting_analysis", label: t("results.lighting") },
    { key: "audio_analysis", label: t("results.audio") },
    { key: "audio_visual_sync", label: t("results.audioVisualSync") },
    { key: "background_analysis", label: t("results.background") },
    { key: "overall_quality", label: t("results.overallQuality") },
    { key: "metadata_analysis", label: t("results.metadata") },
  ];

  const inputLabel =
    result.video_type === "upload" ? t("results.upload") : "URL";
  const locale = i18n.language === "id" ? "id-ID" : "en-US";
  const dateStr = result.created_at
    ? new Date(result.created_at).toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
  const shareUrl = getShareUrl();
  const canShare = isPublic || isSharedView;

  return (
    <main
      className="min-h-screen print:pt-0"
      ref={printRef}
      data-testid="results-page"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 print:py-4">
        {/* Back + Actions bar */}
        <div className="flex items-center justify-between mb-6 print:hidden flex-wrap gap-3">
          {isSharedView ? (
            <Link
              to="/discover"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-discover"
            >
              <ArrowLeft className="w-4 h-4" />{" "}
              {t("nav.discover")}
            </Link>
          ) : (
            <Link
              to="/history"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-history"
            >
              <ArrowLeft className="w-4 h-4" /> {t("nav.history")}
            </Link>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            {/* PDF Download */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              className="rounded-sm gap-1.5 text-xs"
              data-testid="download-pdf-btn"
            >
              <FileDown className="w-3.5 h-3.5" /> {t("results.downloadPdf")}
            </Button>

            {/* Report Badge */}
            {!isSharedView && result && <ReportBadge result={result} />}

            {/* Archive / soft-delete */}
            {!isSharedView && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleArchive}
                disabled={archiving}
                className="rounded-sm gap-1.5 text-xs text-red-400 border-red-500/30 hover:bg-red-500/10 hover:border-red-500"
                data-testid="archive-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {archiving ? "..." : t("results.archiveBtn")}
              </Button>
            )}
          </div>
        </div>

        {/* Shared view banner */}
        {isSharedView && (
          <div className="mb-6 flex items-center justify-between p-3 rounded-sm border border-brand-primary/20 bg-brand-primary/5 print:hidden">
            <span className="text-xs font-mono text-brand-primary">
              {t("results.publicShared")}
            </span>
            {!user && (
              <Link to="/register">
                <Button
                  size="sm"
                  className="rounded-sm bg-brand-primary text-white text-xs"
                >
                  {t("results.signupCta")}
                </Button>
              </Link>
            )}
          </div>
        )}

        {/* Language mismatch indicator */}
        {result?.displayed_language &&
          result.displayed_language !== i18n.language?.split("-")[0] && (
            <div className="mb-4 flex items-center gap-2 p-2.5 rounded-sm border border-amber-500/20 bg-amber-500/5 print:hidden">
              <span className="text-xs font-mono text-amber-400">
                {t("results.langMismatch")} (
                {result.displayed_language.toUpperCase()})
              </span>
            </div>
          )}

        {/* Forensic Report Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            {t("results.forensicReport")}
          </span>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
            {t("results.title")}
          </h1>
        </motion.div>

        {/* ── Two-column layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start">
          {/* ── LEFT: main report content ── */}
          <div className="space-y-6 min-w-0">
            {/* Verdict Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`rounded-md border p-6 ${colors.bg} ${colors.border}`}
              data-testid="verdict-card"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
                {t("results.verdictLabel")}
              </div>
              <div className={`flex items-center gap-3 ${colors.text}`}>
                <VIcon className="w-8 h-8" />
                <span
                  className="font-heading text-3xl font-bold tracking-tight"
                  data-testid="verdict-text"
                >
                  {parseFloat(result.confidence) < 60
                    ? t("results.displayInsufficient")
                    : parseFloat(result.confidence) < 80
                      ? t("results.displayUncertain")
                      : verdict === "FAKE"
                        ? t("results.displayFake")
                        : verdict === "REAL"
                          ? t("results.displayReal")
                          : t("results.displayUncertain")}
                </span>
              </div>
              <p
                className="text-xs text-muted-foreground/70 mt-2 italic"
                data-testid="verdict-disclaimer"
              >
                {t("results.verdictDisclaimer")}
              </p>
              <p
                className="text-sm text-muted-foreground mt-3 leading-relaxed"
                data-testid="summary-text"
              >
                {result.summary}
              </p>
            </motion.div>

            {/* Content Description */}
            {(result.content_title || result.content_description) && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.12 }}
                className="rounded-md border border-border bg-card p-5"
                data-testid="content-info-card"
              >
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
                  {t("results.contentInfo")}
                </div>
                {result.content_title && (
                  <h3
                    className="text-base font-semibold mb-1"
                    data-testid="content-title"
                  >
                    {result.content_title}
                  </h3>
                )}
                {result.content_description && (
                  <p
                    className="text-sm text-muted-foreground leading-relaxed"
                    data-testid="content-description"
                  >
                    {result.content_description}
                  </p>
                )}
              </motion.div>
            )}

            {/* AI Detection Panel */}
            {result.hive_result &&
              !result.hive_result.error &&
              (() => {
                const h = result.hive_result;
                const realPct = (
                  (h.not_ai_generated_score ||
                    1 - (h.ai_generated_score || 0)) * 100
                ).toFixed(1);
                const aiPct = ((h.ai_generated_score || 0) * 100).toFixed(1);
                const dfPct = ((h.deepfake_score || 0) * 100).toFixed(1);
                const audioPct = ((h.ai_audio_score || 0) * 100).toFixed(1);
                const gens = Object.entries(h.generator_scores || {});
                const fallbackGens =
                  result.generation_sources?.map((g) => [g.name, g.score]) ||
                  [];
                const allGens = gens.length > 0 ? gens : fallbackGens;
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.22 }}
                    className="rounded-md border border-border bg-card p-6"
                    data-testid="ai-detection-card"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                        {t("results.detectionTitle")}
                      </div>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
                        ML Classifier
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                      <ScoreBox
                        label={t("results.scoreReal")}
                        value={realPct}
                        color={
                          parseFloat(realPct) >= 50
                            ? "text-verdict-real"
                            : "text-muted-foreground"
                        }
                      />
                      <ScoreBox
                        label={t("results.scoreAiGenerated")}
                        value={aiPct}
                        color={
                          parseFloat(aiPct) >= 90
                            ? "text-verdict-fake"
                            : parseFloat(aiPct) >= 50
                              ? "text-verdict-uncertain"
                              : "text-muted-foreground"
                        }
                      />
                      <ScoreBox
                        label={t("results.scoreDeepfake")}
                        value={dfPct}
                        color={
                          parseFloat(dfPct) >= 90
                            ? "text-verdict-fake"
                            : parseFloat(dfPct) >= 50
                              ? "text-verdict-uncertain"
                              : "text-muted-foreground"
                        }
                      />
                      <ScoreBox
                        label={t("results.scoreAiAudio")}
                        value={audioPct}
                        color={
                          parseFloat(audioPct) >= 50
                            ? "text-verdict-fake"
                            : "text-muted-foreground"
                        }
                      />
                    </div>
                    <GenerationSources
                      gens={allGens}
                      aiPct={aiPct}
                      dfPct={dfPct}
                      t={t}
                    />
                  </motion.div>
                );
              })()}

            {/* Standalone Generation Sources */}
            {(!result.hive_result || result.hive_result.error) && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22 }}
              className="rounded-md border border-border bg-card p-6"
              data-testid="ai-detection-card"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">
                  {t("results.detectionTitle")}
              </div>
                <GenerationSources
                  gens={
                    result.generation_sources?.map((g) => [g.name, g.score]) ||
                    []
                  }
                  t={t}
                />
              </motion.div>
            )}

            {/* Artifacts */}
            {result.artifacts?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="rounded-md border border-border bg-card p-6"
                data-testid="artifacts-card"
              >
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">
                  {t("results.artifacts")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.artifacts.map((a, i) => (
                    <span
                      key={i}
                      className="text-xs font-mono px-2.5 py-1 rounded-sm bg-secondary border border-border"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Technical Breakdown */}
            {Object.keys(tech).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="rounded-md border border-border bg-card p-6"
                data-testid="technical-card"
              >
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">
                  {t("results.technical")}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {techFields.map(({ key, label }) => (
                    <TechRow key={key} label={label} value={tech[key]} />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Recommendations */}
            {result.recommendations?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.32 }}
                className="rounded-md border border-border bg-card p-6"
                data-testid="recommendations-card"
              >
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">
                  {t("results.recommendations")}
                </div>
                <ul className="space-y-2">
                  {result.recommendations.map((rec, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2.5 text-sm text-muted-foreground"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-primary mt-2 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Raw AI Response */}
            {tech.raw_response && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="rounded-md border border-border bg-card overflow-hidden"
              >
                <button
                  onClick={() => setShowRaw(!showRaw)}
                  className="w-full flex items-center justify-between p-4 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="raw-response-toggle"
                >
                  {t("results.rawResponse")}
                  {showRaw ? (
                    <EyeOff className="w-3.5 h-3.5" />
                  ) : (
                    <Eye className="w-3.5 h-3.5" />
                  )}
                </button>
                {showRaw && (
                  <div className="border-t border-border p-4">
                    <pre className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed font-mono overflow-auto max-h-64">
                      {tech.raw_response}
                    </pre>
                  </div>
                )}
              </motion.div>
            )}
          </div>
          {/* end LEFT column */}

          {/* ── RIGHT: thumbnail + confidence + meta ── */}
          <div className="space-y-4 lg:sticky lg:top-[88px] z-10">
            {/* Thumbnail */}
            {result.thumbnail && (
              <div
                data-testid="video-thumbnail"
                onClick={() => setShowThumb(true)}
                style={{
                  cursor: "zoom-in",
                  borderRadius: "6px",
                  overflow: "hidden",
                  border: "1px solid hsl(var(--border))",
                  position: "relative",
                  display: "block",
                  userSelect: "none",
                }}
              >
                <img
                  src={result.thumbnail}
                  alt="Media thumbnail"
                  style={{
                    width: "100%",
                    maxHeight: "240px",
                    display: "block",
                    objectFit: "cover",
                    pointerEvents: "none",
                  }}
                  draggable={false}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: 12,
                    left: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 12px",
                    borderRadius: 4,
                    fontSize: 11,
                    fontFamily: "monospace",
                    fontWeight: 600,
                    pointerEvents: "none",
                  }}
                  className={`${colors.bg} ${colors.border} border ${colors.text}`}
                >
                  <VIcon style={{ width: 14, height: 14 }} />
                  {parseFloat(result.confidence) < 60
                    ? t("results.displayInsufficient")
                    : parseFloat(result.confidence) < 80
                      ? t("results.displayUncertain")
                      : verdict === "FAKE"
                        ? t("results.displayFake")
                        : verdict === "REAL"
                          ? t("results.displayReal")
                          : t("results.displayUncertain")}
                </div>
              </div>
            )}

            {/* Confidence meter */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="rounded-md border border-border bg-card p-5"
              data-testid="confidence-card"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
                {t("results.confidence")}
              </div>
              <ConfidenceMeter
                confidence={result.confidence}
                verdict={verdict}
              />
              <div className="mt-3 text-xs font-mono text-muted-foreground">
                {t("results.detectionCertainty")}
              </div>
            </motion.div>

            {/* Meta */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-md border border-border bg-card p-5"
              data-testid="meta-card"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
                {t("results.meta")}
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    {t("results.videoName")}
                  </span>
                  <span className="font-mono truncate text-right">
                    {result.video_name || "—"}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    {t("results.inputType")}
                  </span>
                  <span className="font-mono">{inputLabel}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    {t("results.aiEngine")}
                  </span>
                  <span className="font-mono text-xs text-right">
                    {result.model_used?.includes("hive")
                      ? "Hive AI + Gemini 2.5 Pro"
                      : "Gemini 2.5 Pro"}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    {t("results.date")}
                  </span>
                  <span className="font-mono text-xs text-right">
                    {dateStr}
                  </span>
                </div>
              </div>
            </motion.div>

            {canSubmitFeedback && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.24 }}
                className="rounded-md border border-border bg-card p-5"
                data-testid="feedback-card"
              >
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
                  {t("results.feedbackTitle")}
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {t("results.feedbackQuestion")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={feedbackValue === "correct" ? "default" : "outline"}
                    className={
                      feedbackValue === "correct"
                        ? "rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5"
                        : "rounded-sm gap-1.5"
                    }
                    disabled={feedbackSaving}
                    onClick={() => handleSubmitFeedback("correct")}
                    data-testid="feedback-correct-btn"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                    {t("results.feedbackCorrect")}
                  </Button>
                  <Button
                    type="button"
                    variant={feedbackValue === "incorrect" ? "default" : "outline"}
                    className={
                      feedbackValue === "incorrect"
                        ? "rounded-sm bg-amber-600 hover:bg-amber-500 text-white gap-1.5"
                        : "rounded-sm gap-1.5"
                    }
                    disabled={feedbackSaving}
                    onClick={() => handleSubmitFeedback("incorrect")}
                    data-testid="feedback-incorrect-btn"
                  >
                    <ThumbsDown className="w-3.5 h-3.5" />
                    {t("results.feedbackIncorrect")}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {feedbackValue
                    ? t("results.feedbackCurrent", {
                        value: t(`results.feedbackValue.${feedbackValue}`),
                      })
                    : t("results.feedbackHint")}
                </p>
              </motion.div>
            )}
          </div>
          {/* end RIGHT column */}
        </div>
        {/* end grid */}

        {/* Bottom actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap gap-3 mt-8 print:hidden"
        >
          {!isSharedView && (
            <Link to="/analyze">
              <Button
                className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white"
                data-testid="analyze-another-btn"
              >
                {t("results.newAnalysis")}
              </Button>
            </Link>
          )}
          <Link
            to={isSharedView ? (user ? "/analyze" : "/register") : "/history"}
          >
            <Button
              variant="outline"
              className="rounded-md"
              data-testid="view-history-btn"
            >
              {isSharedView
                ? user
                  ? t("results.newAnalysis")
                  : t("results.signupCta")
                : t("history.title")}
            </Button>
          </Link>
        </motion.div>
      </div>

      {/* Thumbnail lightbox */}
      {showThumb &&
        result?.thumbnail &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Pratinjau thumbnail"
            onClick={() => setShowThumb(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              backgroundColor: "rgba(0,0,0,0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={() => setShowThumb(false)}
              aria-label="Tutup"
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                background: "rgba(0,0,0,0.5)",
                border: "none",
                color: "white",
                borderRadius: "50%",
                width: 36,
                height: 36,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
              }}
            >
              ✕
            </button>
            <img
              src={result.cover_image || result.thumbnail}
              alt="Media preview"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "90vw",
                maxHeight: "88vh",
                objectFit: "contain",
                borderRadius: 8,
                display: "block",
              }}
            />
          </div>,
          document.body,
        )}

      {/* Print styles */}
      <style>{`
        @media print {
          header, nav, .print\\:hidden { display: none !important; }
          body { background: white !important; color: black !important; }
          .bg-card, .rounded-md { background: #f9f9f9 !important; border: 1px solid #ddd !important; }
          .text-muted-foreground { color: #666 !important; }
        }
      `}</style>
    </main>
  );
}
